import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import type { BandwidthMonthlyTrendPoint } from './bandwidthDashboardTypes';
import {
  bandwidthVerticalBarDim,
  bandwidthVerticalBarPalette,
  bandwidthVerticalBarSelected,
} from './bandwidthChartTheme';
import { formatAxisCompactK, formatDataLabelCompact } from '../kpi-dashboard/chartFormatters';

function monthYearLabelUtc(monthStart: string): string {
  const d = new Date(`${monthStart.slice(0, 10)}T12:00:00.000Z`);
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export interface BandwidthActivityByMonthChartProps {
  title: string;
  subtitle?: string;
  monthlyTrend: BandwidthMonthlyTrendPoint[];
  /** YYYY-MM when a month bar is selected (Dates drill). */
  selectedMonthKey?: string | null;
  onMonthBarClick?: (monthKeyYyyyMm: string) => void;
  emptyHint?: string;
}

const BandwidthActivityByMonthChart: React.FC<BandwidthActivityByMonthChartProps> = ({
  title,
  subtitle,
  monthlyTrend,
  selectedMonthKey = null,
  onMonthBarClick,
  emptyHint,
}) => {
  const { labels, counts, barColors } = useMemo(() => {
    const labelsInner = monthlyTrend.map((m) => monthYearLabelUtc(m.monthStart));
    const countsInner = monthlyTrend.map((m) => m.activityCount);
    const colorsInner = monthlyTrend.map((m, i) => {
      const key = m.monthStart.slice(0, 7);
      if (!selectedMonthKey) return bandwidthVerticalBarPalette(i);
      return key === selectedMonthKey ? bandwidthVerticalBarSelected : bandwidthVerticalBarDim;
    });
    return { labels: labelsInner, counts: countsInner, barColors: colorsInner };
  }, [monthlyTrend, selectedMonthKey]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 20, right: 8, left: 4, bottom: 4 } },
      onClick: (_event: unknown, elements: { index: number }[]) => {
        if (!onMonthBarClick || elements.length === 0) return;
        const idx = elements[0]?.index;
        const point = monthlyTrend[idx];
        if (!point) return;
        onMonthBarClick(point.monthStart.slice(0, 7));
      },
      onHover: (_event: unknown, elements: unknown[], chart: { canvas?: HTMLCanvasElement }) => {
        const canvas = chart?.canvas;
        if (!canvas) return;
        canvas.style.cursor = Array.isArray(elements) && elements.length > 0 ? 'pointer' : 'default';
      },
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed: { y: number | null } }) => {
              const v = ctx.parsed.y;
              return typeof v === 'number' ? `${v.toLocaleString()} activities` : '';
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: '#64748b',
            font: { size: 10 },
            maxRotation: 45,
            minRotation: 45,
          },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(99, 102, 241, 0.14)' },
          ticks: {
            color: '#64748b',
            font: { size: 10 },
            callback: (value: string | number) => formatAxisCompactK(Number(value)),
          },
          title: { display: true, text: 'Activity count', color: '#64748b', font: { size: 11 } },
        },
      },
    }),
    [monthlyTrend, onMonthBarClick]
  );

  if (monthlyTrend.length === 0) {
    return (
      <KpiChartPanel
        title={title}
        titleClassName="!pl-0"
        className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
        flexFill
        bodyClassName="px-3 pb-4 pt-4"
      >
        {subtitle ? <p className="mb-2 text-xs text-slate-500">{subtitle}</p> : null}
        <p className="py-12 text-center text-sm text-slate-500">
          {emptyHint ?? 'No data for the selected filters.'}
        </p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
      flexFill
      bodyClassName="px-3 pb-3 pt-3"
    >
      {subtitle ? <p className="mb-1 text-xs leading-snug text-slate-500">{subtitle}</p> : null}
      <div className="relative h-[260px] w-full min-h-0 flex-1 sm:h-[280px]">
        <Bar
          data={{
            labels,
            datasets: [
              {
                label: 'Activity count',
                data: counts,
                backgroundColor: barColors,
                borderWidth: 0,
                borderRadius: 4,
                maxBarThickness: 36,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 2,
                  clip: false,
                  color: '#334155',
                  font: { size: 10, weight: 600 },
                  formatter: (value: number) => formatDataLabelCompact(value),
                },
              },
            ],
          }}
          options={chartOptions}
        />
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthActivityByMonthChart;
